import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getAllLanguageOptionsByLangCode } from 'lib/courses';
import {
  ApiDownloadButton,
  ApiPostButton,
  DisplayFormattedDate,
  FlagIcon,
  MoreOptions,
  SendToEmailButton
} from 'components/common';
import {
  Box,
  BoxExpandable,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { Icon } from 'components/common/icons';
import { StatCount } from 'components/common/stats';
import { CourseViewVersion } from 'components/courses/common';
import {
  FollowUsersDownloadAnswers,
  FollowUsersEditCompletion
} from 'components/courses/follow';
import { ProfileCoursePreviousCompletionsRemoveCompletion } from '.';

import type { Course, RequestUser } from 'shared-types';
import type { GetUserCoursesCourseRes } from 'shared-types/responses';

interface Props {
  certificateAllowDownloading: boolean;
  completionsAllowViewing: boolean;
  courseLanguageVersions: Course['languageVersions'];
  courseSlug: string;
  courseVersionTagsByVersionNumber: { [version: string]: string | null };
  disableActions?: boolean;
  isUserPage: boolean;
  previousCourseCompletions: GetUserCoursesCourseRes['previousCourseCompletions'];
  tasksAllowDownloading: boolean;
  tasksIncludeReviews: boolean;
  updateUser: () => Promise<void>;
  user?: Pick<RequestUser, 'email' | 'id' | 'username'>;
}

const ProfileCoursePreviousCompletions = ({
  certificateAllowDownloading,
  completionsAllowViewing,
  courseLanguageVersions,
  courseSlug,
  courseVersionTagsByVersionNumber,
  disableActions = true,
  isUserPage,
  previousCourseCompletions,
  tasksAllowDownloading,
  tasksIncludeReviews,
  updateUser,
  user
}: Props) => {
  const {
    language,
    messages,
    pageState: { userId },
    serverConfig: {
      analyticsDatesDisplayTime,
      courseCompletionAllowEdit,
      courseCompletionAllowRemoval,
      videoRequirementsType
    },
    theme,
    user: loggedInUser
  } = useReduxState();

  const languageOptions = getAllLanguageOptionsByLangCode();

  return !previousCourseCompletions?.length ? null : (
    <div className="previous-completions">
      <Box padding={0}>
        <BoxExpandable
          title={
            <div>
              {messages.completionsPreviousCompletions}
              <StatCount
                isLarge={true}
                style={{ display: 'inline-block', marginLeft: 5 }}
                value={previousCourseCompletions.length}
              />
            </div>
          }
        >
          {previousCourseCompletions.length ? (
            <BoxOverflowWrapper>
              <BoxTable>
                <BoxTableHeader>
                  <BoxTableCell>{messages.commonTitle}</BoxTableCell>
                  <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                    {messages.completionsCompletionDate}
                  </BoxTableCell>
                  <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                    {messages.commonExpirationDate}
                  </BoxTableCell>
                  {courseLanguageVersions &&
                    Object.keys(courseLanguageVersions).length > 0 && (
                      <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                        {messages.courseLanguageVersion}
                      </BoxTableCell>
                    )}
                  <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                    {messages.completionWorkload}
                  </BoxTableCell>
                  <BoxTableCell>{messages.tasksReading}</BoxTableCell>
                  <BoxTableCell>{messages.tasksVideos}</BoxTableCell>
                  <BoxTableCell style={{ whiteSpace: 'nowrap' }}>
                    {messages.tasksTaskPoints}
                  </BoxTableCell>
                  <BoxTableCell sticky={true} stickyAnchor="right" />
                </BoxTableHeader>

                {previousCourseCompletions.map((completion) => (
                  <BoxTableRow key={completion.id}>
                    <BoxTableCell
                      label={messages.commonTitle}
                      style={{ minWidth: 300 }}
                    >
                      <div aria-label={messages.commonTag}>
                        {completion.courseTitle}

                        {courseVersionTagsByVersionNumber?.[
                          completion.courseVersion
                        ] ? (
                          <div className="subtitle">
                            <div className="subtitle-icon">
                              <Icon icon="tag" />
                            </div>
                            {
                              courseVersionTagsByVersionNumber[
                                completion.courseVersion
                              ]
                            }
                          </div>
                        ) : null}
                      </div>
                    </BoxTableCell>
                    <BoxTableCell label={messages.completionsCompletionDate}>
                      <DisplayFormattedDate
                        date={completion.completionDate}
                        withTime={analyticsDatesDisplayTime}
                      />
                    </BoxTableCell>
                    <BoxTableCell label={messages.commonExpirationDate}>
                      <DisplayFormattedDate date={completion.expirationDate} />
                    </BoxTableCell>
                    {courseLanguageVersions &&
                      Object.keys(courseLanguageVersions).length > 0 && (
                        <BoxTableCell label={messages.courseLanguageVersion}>
                          {/* a completion in theory might not have a language version
                        if an admin grants a completion before the user can select a language */}
                          {completion.courseLanguageVersion && (
                            <FlagIcon
                              lang={completion.courseLanguageVersion}
                              tooltipText={
                                languageOptions[
                                  completion.courseLanguageVersion
                                ]?.capitalizedName
                              }
                            />
                          )}
                        </BoxTableCell>
                      )}
                    <BoxTableCell label={messages.completionWorkload}>
                      {new Intl.NumberFormat(language).format(
                        completion.courseWorkload
                      )}
                    </BoxTableCell>
                    <BoxTableCell label={messages.tasksReading}>
                      {`${Math.round(completion.readingProgress * 100)}%`}
                    </BoxTableCell>
                    <BoxTableCell label={messages.tasksVideos}>
                      {videoRequirementsType === 'individual'
                        ? completion.videosWatched
                        : `${Math.round(completion.videoProgress * 100)}%`}
                    </BoxTableCell>
                    <BoxTableCell label={messages.tasksTaskPoints}>
                      {`${Math.round(completion.tasksProgress * 100)}%`}
                    </BoxTableCell>
                    <BoxTableCell
                      shrink={true}
                      sticky={true}
                      stickyAnchor="right"
                      stickyBackgroundColor="white"
                    >
                      <MoreOptions
                        renderInPortal={true}
                        styleContentButtons={false}
                      >
                        {courseSlug && completionsAllowViewing ? (
                          <CourseViewVersion
                            completionId={completion.id}
                            courseSlug={courseSlug}
                            courseVersion={completion.courseVersion}
                          />
                        ) : null}

                        {isUserPage && (
                          <>
                            {!disableActions && courseCompletionAllowEdit ? (
                              <FollowUsersEditCompletion
                                completion={completion}
                                isDropdown={true}
                                userId={isUserPage ? userId : loggedInUser.id}
                                updateUsers={updateUser}
                              />
                            ) : null}

                            {!disableActions && courseCompletionAllowRemoval ? (
                              <ProfileCoursePreviousCompletionsRemoveCompletion
                                completionId={completion.id}
                                updateUser={updateUser}
                                userId={isUserPage ? userId : loggedInUser.id}
                              />
                            ) : null}
                          </>
                        )}

                        {isUserPage || certificateAllowDownloading ? (
                          <ApiDownloadButton
                            filename={`${completion.courseTitle}.pdf`}
                            icon="file-pdf"
                            isDropdown={true}
                            text={messages.completionsDownloadCertificate}
                            url={
                              `/admin/certificate` +
                              `?completionId=${completion.id}` +
                              `&language=${language}`
                            }
                          />
                        ) : null}

                        {isUserPage || tasksAllowDownloading ? (
                          <FollowUsersDownloadAnswers
                            completionId={completion.id}
                            courseId={completion.CourseId}
                            includeReviews={tasksIncludeReviews}
                            isAdmin={Boolean(isUserPage)}
                            isDropdown={true}
                            user={user}
                          />
                        ) : null}

                        {isUserPage || certificateAllowDownloading ? (
                          <>
                            {loggedInUser?.email ? (
                              <ApiPostButton
                                data={{
                                  completionId: completion.id,
                                  emailAddress: loggedInUser.email,
                                  language
                                }}
                                icon="envelope"
                                isDropdown={true}
                                successMessage={
                                  messages.completionsSendCertificateSuccess
                                }
                                text={messages.completionsSendCertificate}
                                url="/admin/certificate/send"
                              />
                            ) : (
                              <SendToEmailButton
                                apiData={{
                                  completionId: completion.id,
                                  language
                                }}
                                apiUrl="/admin/certificate/send"
                                buttonText={messages.completionsSendCertificate}
                                id="certificate"
                                isDropdown={true}
                                successMessage={
                                  messages.completionsSendCertificateSuccess
                                }
                              />
                            )}
                          </>
                        ) : null}

                        {isUserPage || tasksAllowDownloading ? (
                          <>
                            {loggedInUser?.email ? (
                              <ApiPostButton
                                data={{ completionId: completion.id, language }}
                                icon="envelope"
                                isDropdown={true}
                                successMessage={
                                  messages.tasksSendAnswersSuccess
                                }
                                text={messages.tasksSendAnswers}
                                url={`/admin/users/${user?.id}/courses/${completion.CourseId}/answers/send`}
                              />
                            ) : (
                              <SendToEmailButton
                                apiData={{
                                  completionId: completion.id,
                                  language
                                }}
                                apiUrl={`/admin/users/${user?.id}/courses/${completion.CourseId}/answers/send`}
                                buttonText={messages.tasksSendAnswers}
                                id="answers"
                                isDropdown={true}
                                successMessage={
                                  messages.tasksSendAnswersSuccess
                                }
                              />
                            )}
                          </>
                        ) : null}
                      </MoreOptions>
                    </BoxTableCell>
                  </BoxTableRow>
                ))}
              </BoxTable>
            </BoxOverflowWrapper>
          ) : null}
        </BoxExpandable>
      </Box>
      <style jsx>{`
        .subtitle {
          align-items: flex-start;
          color: ${theme.textColorLighter};
          display: flex;
          font-size: 1.2rem;
          font-weight: 600;
          gap: 5px;
          margin-top: 2px;
        }
        .subtitle-icon {
          font-size: 1rem;
          position: relative;
          top: 2px;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .inner {
            padding: 5px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProfileCoursePreviousCompletions;
